import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller, FormProvider, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PhoneInput, {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import imageCompression from "browser-image-compression";
import "react-phone-number-input/style.css";
import SidebarLayout from "@/Components/sidebar/SidebarLayout";
import {
  addBusiness,
  editBusiness,
  fetchStripeProducts,
  getRestaurantDetailById,
  uploadBusinessImages,
} from "@/utils";
import Swal from "sweetalert2";
import { BUSINESS_SUBTYPES } from "@/constants";

import "@/Components/accounts/custom_stripe.css";
import { axiosInstanceWithToken } from "@/helpers/axios";
import SimpleModal from "@/Components/modal/SimpleModal";
import { chatServerInstance } from "@/helpers";
import { ImageCard } from "./ImageCard";
import BusinessDetail from "./components/BusinessDetail";
import Summary from "./components/Summary";
import Payment from "./components/Payment";
import LocationForm from "./components/LocationForm";
import ImageUploadingComponent from "./components/ImageUploadingComponent";
import { useNavigate, useParams } from "react-router-dom";
import SelectPaymentPlan from "./components/SelectPaymentPlan";
import SuccessModal from "./components/SuccessModal";
import DiscountFields from "./components/discount";

const TAX_RATE = 0.0625;
const BASE_PRICE = 10;

export const createStripeSubscription = async ({
  customer_id,
  payment_method_id,
  product_id,
}) => {
  const response = await axiosInstanceWithToken.post("/create-subscription", {
    customer_id,
    payment_method_id,
    product_id,
  });
  return response.data;
};

function transformToPriceIdArray(input) {
  return Object.entries(input).map(([type, data]) => ({
    price_id: data.value.price_id,
    type,
  }));
}

export const AddBusinesses = () => {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [imagesMarkedForDeletion, setImagesMarkedForDeletion] = useState([]);
  const { id: businessId } = useParams();
  const [selectedPlansAndBilling, setSelectedPlansAndBilling] = useState({});
  const modalRef = useRef(null);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState({});

  const [plans, setPlans] = useState([]); // All fetched Stripe plans

  const [billingCycle, setBillingCycle] = useState({
    freq: "",
    value: null,
    tiers: "",
  }); // 'monthly' or 'yearly'

  const schema = yup.object().shape({
    menu_images: yup
      .mixed()
      .test(
        "maxLength",
        "You can upload up to 20 menu images",
        (value) => !value || value.length <= 20
      ),

    tag_line: yup
      .string()
      .required("Tagline is required")
      .max(100, "Tagline must be at most 100 characters"),
    business_details: yup
      .string()
      .required("Description is required")
      .min(5, "Description must be at least 5 characters")
      .max(1000, "Description must be at most 1000 characters"),
    address: yup
      .string()
      .required("Address is required")
      .max(300, "Address must be at most 100 characters"),
    city: yup
      .string()
      .required("City is required")
      .max(50, "City must be at most 50 characters"),
    state: yup
      .string()
      .required("State is required")
      .max(50, "State must be at most 50 characters"),
    zipcode: yup.string().required("ZIP/Postal Code is required"),
    country: yup
      .string()
      .required("Country is required")
      .max(50, "Country must be at most 50 characters"),
    latitude: yup
      .number()
      .typeError("Latitude must be a number")
      .required("Latitude is required")
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    longitude: yup
      .number()
      .typeError("Longitude must be a number")
      .required("Longitude is required")
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
    name: yup.string().required("Name is required"),
    distance_from_runway: yup
      .string()
      .required("Distance from runway is required"),
    type: businessId
      ? yup.string().required("Business type is required")
      : yup
          .array()
          .of(yup.string())
          .min(1, "Please select at least one business type")
          .required("Business types are required"),
    airport: yup
      .string()
      .required("Airport Identifier is required")
      .max(4, "Airport Identifier cannot exceed 4 characters"),
    url: yup.string().url("Invalid URL").required("Website URL is required"),
    operational_hrs: yup
      .string()
      .required("Hours of operation is required")
      .max(100, "Hours of operation must be at most 100 characters"),
    phone: yup
      .string()
      .required("Cell phone number is required")
      .test(
        "is-valid-phone",
        "Please enter all required digits and make it valid",
        (value) =>
          value &&
          isPossiblePhoneNumber(value) &&
          isValidPhoneNumber(value || "")
      )
      .test(
        "unique-phone",
        "Primary phone and Other phone cannot be the same",
        function (value) {
          const { other_phone } = this.parent;
          return !value || !other_phone || value !== other_phone;
        }
      ),
  });
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      distance_from_runway: 0,
      airport: "",
      url: "https://",
      phone: "",
      operational_hrs: "",
      // type: ["aircraft_sales_new", "on_demand_air_taxi_flights"],
      type: [],
      address: "",
      city: "",
      name: "",
      tag_line: "",
      state: "",
      country: "",
      zipcode: "",
      latitude: "",
      longitude: "",
      logo_image: null,
      business_details: "",
      featured: false,
      photo_images: [],
      menu_images: [],
      url_protocol_selection: "https://",
      discounts: [
        {
          title: "",
          description: "",
        },
      ],
    },
  });
  console.log({ selectedPlansAndBilling, selectedPlanIndex });
  const {
    setValue,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    watch,
    reset,
  } = methods;
  const businessType = watch("type");

  // Watch for changes in the 'url_protocol_selection' field
  const selectedProtocol = useWatch({
    control,
    name: "url_protocol_selection",
    defaultValue: "https://",
  });

  // Watch for changes in the 'url' field itself
  const currentUrlValue = useWatch({
    control,
    name: "url",
    defaultValue: "https://", // Ensure initial value for watching
  });

  // Effect to sync the 'url' field with the selected protocol
  // This ensures that when the protocol radio button is changed,
  // the 'url' field is updated with the correct prefix,
  // while keeping the existing domain part.
  useEffect(() => {
    // Get the current domain part without any protocol
    const domainPart = currentUrlValue.replace(/^https?:\/\//, "");
    setValue("url", `${selectedProtocol}${domainPart}`);
  }, [selectedProtocol, setValue]); // Only re-run when selectedProtocol changes

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetchStripeProducts();
        setPlans(res || []);
      } catch (error) {
        console.error("Failed to fetch Stripe products:", error);
        setPlans([]); // Set to empty array on error
      } finally {
      }
    };
    fetchPlans();
  }, []);

  const [compressingMenuImages, setCompressingMenuImages] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [showStripe, setShowStripe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const userId = JSON.parse(localStorage.getItem("data"))?.id;
  const [isModalOpen, setIsModalOpen] = useState(false); // business modal
  const [currentStep, setCurrentStep] = useState(1);
  const photoImages = watch("photo_images") || [];
  const businessDetails = watch("business_details") || "";

  const [logoPreview, setLogoPreview] = useState(null); // This holds the data URL for preview
  const [compressingLogo, setCompressingLogo] = useState(false); // Indicator for logo compression

  console.log("getValues", { clientSecret }, getValues());

  const taxAmount = BASE_PRICE * TAX_RATE;
  const total = BASE_PRICE + taxAmount;

  // Fetch client secret for PaymentElement
  const fetchClientSecret = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate total (price + tax)
      // const product_id = billingCycle?.value?.product_id;
      const payload = {};
      const response = await chatServerInstance.post(
        "/business/payment/setup-intent",
        payload
      );
      setClientSecret(response?.data?.client_secret);
    } catch (err) {
      // fallback error
    } finally {
      setLoading(false);
    }
  }, [billingCycle]);

  const fetchRestaurantDetailById = useCallback(
    async (businessId) => {
      setLoadingData(true);
      getRestaurantDetailById(businessId)
        .then((data) => {
          console.log("edit Data", { data });

          const { images, business_logo, url, discounts, ...values } = data;
          const photo_images = images.filter((photo) => photo.type === "photo");
          const menu_images = images.filter((photo) => photo.type === "menu");
          console.log({ photo_images, menu_images });
          let updatedDiscount = [];
          try {
            updatedDiscount = discounts
              ? JSON.parse(discounts)
              : [{ title: "", description: "" }];
          } catch (error) {
            console.log({ error });
            updatedDiscount = [{ title: "", description: "" }];
          }

          // Determine protocol
          const protocol = url?.startsWith("https://")
            ? "https://"
            : url?.startsWith("http://")
            ? "http://"
            : ""; // fallback
          reset({
            ...values,
            ...(photo_images && photo_images?.length > 0 && { photo_images }),
            ...(menu_images && menu_images?.length > 0 && { menu_images }),
            url,
            discounts: updatedDiscount ?? [{ title: "", description: "" }],
            ...(protocol && { url_protocol_selection: protocol }),
            ...(business_logo && {
              logo_image: `https://s3.amazonaws.com/flyinn-app-bucket/${business_logo}`,
            }),
          });
          setLoadingData(false);
        })
        .catch((err) => {
          console.log({ err });
          setLoadingData(false);
        });
    },
    [businessId, reset]
  );

  useEffect(() => {
    if (!clientSecret && currentStep && currentStep === 4) {
      fetchClientSecret();
    }
  }, [showStripe, clientSecret, fetchClientSecret, currentStep]);

  useEffect(() => {
    if (businessId) {
      fetchRestaurantDetailById(businessId);
    }
  }, [businessId]);

  // Initialize selectedPlanIndex and selectedPlansAndBilling based on businessTypes
  useEffect(() => {
    const initialSelectedPlanIndex = {};
    const initialSelectedPlansAndBilling = {};
    console.log("businessType-===", { businessType });
    !businessId &&
      Array.isArray(businessType) &&
      businessType?.length > 0 &&
      businessType?.forEach((type) => {
        if (!selectedPlanIndex[type]) {
          initialSelectedPlanIndex[type] = null; // No plan selected initially for each type
        }
        if (!selectedPlansAndBilling[type]) {
          initialSelectedPlansAndBilling[type] = {
            freq: "",
            value: null,
            tiers: "",
          };
        }
      });

    setSelectedPlanIndex((prev) => ({
      ...prev,
      ...initialSelectedPlanIndex,
    }));
    setSelectedPlansAndBilling((prev) => ({
      ...prev,
      ...initialSelectedPlansAndBilling,
    }));
  }, [businessType]); // Re-run if businessTypes change

  const onSubmit = async (data) => {
    // Get user info from localStorage
    const userData = JSON.parse(localStorage.getItem("data"));
    let paymentCustomerId = userData?.payment_customer_id;
    // If no Stripe customer, create one
    console.log({ paymentCustomerId, userData });
    if (!paymentCustomerId) {
      try {
        setCreatingCustomer(true);
        const createRes = await chatServerInstance.post(
          "/business/payment/create-customer",
          {
            name: userData?.name || data.name,
            email: userData?.email,
          }
        );
        setCreatingCustomer(false);
        if (createRes?.data?.customer_id) {
          paymentCustomerId = createRes.data.customer_id;
          // Update localStorage and userId
          userData.payment_customer_id = paymentCustomerId;
          localStorage.setItem("data", JSON.stringify(userData));
        } else {
          setCreatingCustomer(false);
          Swal.fire(
            "Error",
            "Failed to create Stripe customer. Please try again.",
            "error"
          );
          return;
        }
      } catch (err) {
        Swal.fire(
          "Error",
          "Failed to create Stripe customer. Please try again.",
          "error"
        );
        setCreatingCustomer(false);
        return;
      }
    }
    // Attach images to businessData
    setBusinessData({ ...data });

    setIsModalOpen(true);
    setCurrentStep(1);
  };

  // Called after payment is successful
  const handleBusinessAdd = async (payment_method_id) => {
    let businessPayload;

    const formValues = { ...getValues() };
    const { photo_images, menu_images, logo_image, ...rest } = formValues || {};

    const priceIds = transformToPriceIdArray(selectedPlansAndBilling);
    console.log({ priceIds, selectedPlansAndBilling });

    businessPayload = {
      ...rest,
      user_id: userId,
      total,
      payment_method_id,
      price_ids: priceIds,
      // price_ids: [
      //   {
      //     price_id: "price_1Rl90yA4ox7ufUXKXW0ZZeuB",
      //     type: "aircraft_sales_new",
      //   },
      //   {
      //     price_id: "price_1Rg7RJA4ox7ufUXKnf8CP1vv",
      //     type: "on_demand_air_taxi_flights",
      //   },
      // ],
    };

    const response = await addBusiness(businessPayload);

    console.log({ response }, "test");
    const apiData = response?.data?.data || [];

    const updated_photo_images =
      photo_images?.length > 0 ? photo_images.map((item) => item?.file) : [];

    for (const business of apiData) {
      const { business_id } = business;

      // Upload photo_images and logo_image (if any)
      await uploadBusinessImages({
        business_id,
        ...(updated_photo_images.length > 0 && {
          photo_images: updated_photo_images,
        }),
        ...(logo_image && { logo_image }),
      });

      // Upload menu_images (if any)
      if (menu_images?.length > 0) {
        await uploadBusinessImages({
          business_id,
          menu_images,
        });
      }
    }
  };
  // Called after payment is successful
  const updateBusiness = async () => {
    try {
      let businessPayload;

      const formValues = { ...getValues() };
      const { photo_images, menu_images, logo_image, ...rest } =
        formValues || {};
      businessPayload = {
        ...rest,
        user_id: userId,
        ...(imagesMarkedForDeletion?.length > 0 && {
          remove_images: imagesMarkedForDeletion,
        }),
      };
      setLoading(true);
      await editBusiness(businessId, businessPayload);

      let updated_photo_images = photo_images?.filter((item) => item?.file);

      const updated_menu_images = menu_images?.filter(
        (item) => item instanceof File
      );

      console.log(
        "logo_image",
        logo_image,
        typeof logo_image,
        logo_image instanceof File
      );

      if (updated_photo_images?.length > 0) {
        updated_photo_images = updated_photo_images.map((item) => item?.file);
        await uploadBusinessImages({
          business_id: businessId,
          photo_images: updated_photo_images,
        });
      }
      if (
        logo_image &&
        (logo_image instanceof File || logo_image instanceof Blob)
      ) {
        await uploadBusinessImages({
          business_id: businessId,
          logo_image: logo_image,
        });
      }
      if (updated_menu_images?.length > 0) {
        await uploadBusinessImages({
          business_id: businessId,
          menu_images: updated_menu_images,
        });
      }
      setShowSuccessModal(true);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Stepper UI
  const steps = [
    { label: "Business Details" },
    { label: "Select payment plan" },
    { label: "Review & Summary" },
    { label: "Payment" },
  ];

  const renderStepper = () => (
    <div className="w-full mb-8">
      <div className="flex items-center">
        {steps.map((step, idx) => (
          <React.Fragment key={step.label}>
            {/* Step circle and label */}
            <div className="flex flex-col items-center z-10">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-full border-4 transition-all duration-300 ${
                  currentStep === idx + 1
                    ? "border-[#af2322] bg-white text-[#af2322] font-bold shadow-lg"
                    : idx + 1 < currentStep
                    ? "border-[#8ec639] bg-[#8ec639] text-white"
                    : "border-gray-200 bg-white text-gray-400"
                }`}
              >
                <span className="text-lg font-semibold">{idx + 1}</span>
              </div>
              <span
                className={`mt-2 text-sm font-medium text-center max-w-[100px] ${
                  currentStep === idx + 1
                    ? "text-[#af2322]"
                    : idx + 1 < currentStep
                    ? "text-[#8ec639]"
                    : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line (except for last step) */}
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-1 mx-[-2px] ${
                  idx + 1 < currentStep ? "bg-[#8ec639]" : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Improved modal content logic
  const renderModalContent = () => {
    if (!businessData || businessId) return null;
    return (
      <div
        className={`w-full ${
          currentStep === 2 ? "" : "max-w-2xl"
        } px-0 md:px-2 py-4 mx-auto overflow-y-auto`}
        style={{ maxHeight: "90vh" }}
      >
        {renderStepper()}
        {currentStep === 1 && (
          <BusinessDetail
            businessData={businessData}
            setIsModalOpen={setIsModalOpen}
            setShowStripe={setShowStripe}
            setCurrentStep={setCurrentStep}
          />
        )}
        {currentStep === 2 && (
          <SelectPaymentPlan
            setCurrentStep={setCurrentStep}
            taxAmount={taxAmount}
            total={total}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            businessTypes={businessType}
            selectedPlansAndBilling={selectedPlansAndBilling}
            setSelectedPlansAndBilling={setSelectedPlansAndBilling}
            plans={plans}
            selectedPlanIndex={selectedPlanIndex}
            setSelectedPlanIndex={setSelectedPlanIndex}
            // State to manage selected plan index for each business type
            // Example: { 'Aircraft Sales - New': 0, 'Leasing & Fractional Ownership': 1 } (0 for SILVER, 1 for GOLD)
          />
        )}
        {currentStep === 3 && (
          <Summary
            setCurrentStep={setCurrentStep}
            taxAmount={taxAmount}
            total={total}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            getValues={getValues}
            setValue={setValue}
            setSelectedPlansAndBilling={setSelectedPlansAndBilling}
            setSelectedPlanIndex={setSelectedPlanIndex}
            selectedPlansAndBilling={selectedPlansAndBilling}
            selectedPlanIndex={selectedPlanIndex}
          />
        )}
        {currentStep === 4 && clientSecret && (
          <Payment
            clientSecret={clientSecret}
            businessData={businessData}
            handleBusinessAdd={handleBusinessAdd}
            setIsModalOpen={setIsModalOpen}
            setShowStripe={setShowStripe}
            setCurrentStep={setCurrentStep}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            setShowSuccessModal={setShowSuccessModal}
            selectedPlansAndBilling={selectedPlansAndBilling}
          />
        )}
      </div>
    );
  };

  // Compression function for a single logo image
  const compressLogoImage = async (file) => {
    const options = {
      maxSizeMB: 0.5, // Target 1MB for the binary logo file
      useWebWorker: true,
      maxWidthOrHeight: 800, // Logos typically don't need very high dimensions
      fileType: file.type,
    };

    try {
      const compressedFile = await imageCompression(file, options);

      return compressedFile;
    } catch (error) {
      console.error("Logo compression error:", error);
      return file; // Return original file if compression fails
    }
  };

  const handleLogoChange = async (e) => {
    const originalFile = e.target.files?.[0];

    if (!originalFile) {
      setValue("logo_image", null, { shouldValidate: true });
      setLogoPreview(null);
      return;
    }

    // Basic validation
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(originalFile.type)) {
      alert("Only JPG, JPEG, PNG, and WEBP images are allowed for the logo.");
      e.target.value = null;
      setValue("logo_image", null, { shouldValidate: true });
      setLogoPreview(null);
      return;
    }

    setCompressingLogo(true);

    try {
      let processedLogoFile = originalFile;

      if (originalFile.size > 0.5 * 1024 * 1024) {
        processedLogoFile = await compressLogoImage(originalFile);
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(processedLogoFile);

      // Set the File object in form state
      setValue("logo_image", processedLogoFile, { shouldValidate: true });
    } catch (error) {
      console.error("Error processing logo image:", error);
      alert("Failed to process logo image. Please try again.");
      setValue("logo_image", null, { shouldValidate: true });
      setLogoPreview(null);
    } finally {
      setCompressingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setValue("logo_image", null, { shouldValidate: true });
    setLogoPreview(null);
  };
  const handleMenuImagesChange = async (e, fieldOnChange, values) => {
    console.log({ values });
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) {
      // If no new files selected (e.g., user canceled file dialog)
      e.target.value = null; // Clear input value
      return;
    }

    const currentFiles = values || []; // Get current files from react-hook-form state

    // Pre-compression validation
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    const invalidFiles = newFiles.filter(
      (file) => !validTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      alert(
        "Some files were invalid. Only JPG, PNG, WEBP images are allowed, and each file must be smaller than 15MB before optimization."
      );
      e.target.value = null; // Clear the input
      return;
    }

    // Determine available slots based on maxNumber (20 in your example)
    const availableSlots = 20 - currentFiles.length;
    const filesToProcess = newFiles.slice(0, availableSlots);

    if (filesToProcess.length === 0 && availableSlots === 0) {
      alert("You have reached the maximum limit of 20 menu images.");
      e.target.value = null; // Clear the input
      return;
    }

    setCompressingMenuImages(true); // Show compressing indicator

    const processedNewFiles = [];

    try {
      for (const originalFile of filesToProcess) {
        let compressedFile = originalFile;

        // Only compress if the file size is greater than 1MB
        if (originalFile.size > 0.5 * 1024 * 1024) {
          compressedFile = await compressLogoImage(originalFile);
        } else {
          console.log(
            `Menu image within size limit: ${(
              originalFile.size /
              1024 /
              1024
            ).toFixed(2)} MB`
          );
        }

        // Ensure it's a File object with original name, even if browser-image-compression returns a Blob
        let finalFile;
        if (compressedFile instanceof File) {
          finalFile = compressedFile;
        } else if (compressedFile instanceof Blob) {
          const originalName =
            originalFile.name ||
            `menu_image_${Date.now()}.${
              compressedFile.type.split("/")[1] || "jpeg"
            }`;
          finalFile = new File([compressedFile], originalName, {
            type: compressedFile.type,
            lastModified: Date.now(),
          });
          console.log(
            `Converted menu image Blob to File. New File name: ${finalFile.name}`
          );
        } else {
          console.warn(
            "Unexpected menu image file type after processing:",
            typeof compressedFile,
            compressedFile
          );
          finalFile = originalFile; // Fallback to original
        }

        processedNewFiles.push(finalFile);
      }

      // Update react-hook-form state with the new array of compressed File objects
      fieldOnChange([...currentFiles, ...processedNewFiles]);
    } catch (error) {
      console.error("Error processing menu images:", error);
      alert("Failed to process some menu images. Please try again.");
      // Optionally, revert to previous state or handle partial success
    } finally {
      setCompressingMenuImages(false); // Hide compressing indicator
      e.target.value = null; // Clear the input to allow selecting the same file(s) again
    }
  };
  const isEditMode = Boolean(businessId);
  if (loadingData) return "Loading...";

  return (
    <SidebarLayout>
      <div className="container px-4 md:px-8 !mt-20">
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-8 bg-white rounded-xl shadow-sm p-6"
          >
            {/* Location Section */}
            <LocationForm />

            {/* Business Photos Section */}
            <div className="border-b pb-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 !mb-2 flex items-center">
                  <i className="fa fa-images text-purple-500 mr-2"></i>
                  Business Photos
                </h3>

                <p className="text-sm text-blue-800">
                  <i className="fa fa-info-circle mr-2"></i>
                  This section is **optional**, but we highly recommend
                  uploading at least 5 high-quality photos showcasing your
                  business. Photos significantly help customers understand your
                  offerings and atmosphere.
                </p>

                <Controller
                  name="photo_images"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div className="space-y-3">
                      <ImageUploadingComponent
                        value={value}
                        onChange={onChange}
                        setImagesMarkedForDeletion={setImagesMarkedForDeletion}
                      />
                      {value && value?.length > 0 && (
                        <small className="text-sm !mt-0 !mb-2 text-gray-500 flex items-center gap-2 italic">
                          <i
                            className="fa fa-info-circle"
                            aria-hidden="true"
                          ></i>
                          Click the star under the image you want as your main
                          photo. The first one will be the main photo by default
                          if no star is clicked.
                        </small>
                      )}

                      {error && (
                        <p className="text-red-500 text-sm font-medium mt-2 flex items-center">
                          <i className="fa fa-exclamation-circle mr-2"></i>
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <div className="flex items-center mb-4">
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {photoImages.length} photos uploaded
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          photoImages.length >= 5
                            ? "text-green-600"
                            : "text-orange-500"
                        }`}
                      >
                        {photoImages.length >= 5
                          ? "✓ Recommendation met"
                          : "Recommendation pending"}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${
                          photoImages.length >= 5
                            ? "bg-green-500"
                            : "bg-orange-400"
                        }`}
                        style={{
                          width: `${Math.min(
                            100,
                            (photoImages.length / 5) * 100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Details Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Upload */}
              <div className="md:col-span-1">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fa fa-image text-blue-500 mr-2"></i>
                    Business Logo
                  </label>
                  <p className="text-sm text-gray-500 !mb-3">
                    Upload your business logo. This will be displayed on your
                    listing.
                  </p>

                  <>
                    {compressingLogo && (
                      <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg">
                          <p className="text-lg font-medium">
                            Optimizing logo...
                          </p>
                          <p className="text-gray-600">
                            Please wait while we process your logo image.
                          </p>
                        </div>
                      </div>
                    )}

                    <Controller
                      name="logo_image"
                      control={control}
                      render={({ field }) => {
                        // Determine if we have a URL (edit mode) or File (create mode)
                        const isUrl = typeof field.value === "string";

                        return (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center">
                              <label
                                htmlFor="logo_image-upload"
                                className="cursor-pointer flex items-center justify-center px-4 py-3 border border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors w-full"
                              >
                                <div className="text-center">
                                  <i className="fa fa-cloud-upload-alt text-gray-400 text-2xl mb-2"></i>
                                  <p className="text-sm text-gray-700 mb-0 text-center">
                                    {field.value
                                      ? "Change Logo"
                                      : "Click to upload logo"}
                                  </p>
                                  <p className="text-xs text-center text-gray-500 mt-1 !mb-1">
                                    JPG, PNG, WEBP, JPEG - Images over 1MB will
                                    be automatically optimized.
                                  </p>

                                  <div className="!text-center ">
                                    <span className="custom_image_button">
                                      Select and upload
                                    </span>
                                  </div>
                                </div>
                                <input
                                  id="logo_image-upload"
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/jpg"
                                  className="hidden"
                                  onChange={handleLogoChange}
                                />
                              </label>
                            </div>

                            {/* Logo Preview Section */}
                            {field.value && (
                              <div className="relative mt-3 w-fit">
                                <img
                                  src={isUrl ? field.value : logoPreview}
                                  alt="Logo Preview"
                                  className="h-24 w-24 object-contain rounded-md border border-gray-200 shadow-sm transition-transform duration-200 hover:scale-[1.02]"
                                />
                                <button
                                  type="button"
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-7 h-7 flex items-center justify-center shadow-md hover:bg-red-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                                  onClick={handleRemoveLogo}
                                  title="Remove Logo"
                                >
                                  <i className="fa fa-times text-sm"></i>
                                </button>
                              </div>
                            )}

                            {errors.logo_image && (
                              <p className="text-red-500 text-sm mt-2">
                                <i className="fa fa-exclamation-circle mr-1"></i>
                                {errors.logo_image.message}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                  </>
                </div>
              </div>

              {/* Business Name */}
              <div className="md:col-span-1">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fa fa-store text-blue-500 mr-2"></i>
                    Business Name
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <p className="text-sm text-gray-500 !mb-3">
                    The official name of your business as customers will see it.
                  </p>

                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <div className="relative">
                        <input
                          {...field}
                          type="text"
                          placeholder="e.g. Skyline Restaurant"
                          className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm mt-2">
                            <i className="fa fa-exclamation-circle mr-1"></i>
                            {errors.name.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Tagline */}
              <div className="md:col-span-1">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fa fa-tag text-blue-500 mr-2"></i>
                    Business Tagline
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <p className="text-sm text-gray-500 !mb-3">
                    A short, catchy phrase that describes your business (max 100
                    characters).
                  </p>

                  <Controller
                    name="tag_line"
                    control={control}
                    render={({ field }) => (
                      <div className="relative">
                        <input
                          {...field}
                          type="text"
                          placeholder="e.g. The best views in town!"
                          className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          maxLength={100}
                        />
                        <div className="text-xs text-gray-500 mt-1 text-right">
                          {field.value?.length || 0}/100 characters
                        </div>
                        {errors.tag_line && (
                          <p className="text-red-500 text-sm mt-2">
                            <i className="fa fa-exclamation-circle mr-1"></i>
                            {errors.tag_line.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Business Type */}
              <div className="md:col-span-1">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fa fa-building text-blue-500 mr-2"></i>
                    Business Type
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <p className="text-sm text-gray-500 !mb-3">
                    Select the category that best describe your business.
                  </p>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowModal(true)}
                      className="w-full border border-solid border-gray-300 rounded-md px-4 py-3 bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {(() => {
                        const isEditMode = !!businessId;
                        const selectedTypes = isEditMode
                          ? [businessType]
                          : businessType || [];

                        if (!selectedTypes.length) {
                          return "Select Business Types";
                        }

                        const allSubcategories = BUSINESS_SUBTYPES.flatMap(
                          (group) => group.subcategories
                        );

                        const selectedLabels = selectedTypes
                          ?.map(
                            (type) =>
                              allSubcategories.find((sub) => sub.type === type)
                                ?.label
                          )
                          .filter(Boolean);

                        const visibleLabels = selectedLabels.slice(0, 2);
                        const remainingCount =
                          selectedLabels.length - visibleLabels.length;

                        return (
                          <>
                            {visibleLabels.join(", ")}
                            {remainingCount > 0 && ` +${remainingCount} more`}
                          </>
                        );
                      })()}
                    </button>
                    <i className="absolute right-4 top-4 text-gray-400 fa fa-chevron-down"></i>

                    {errors.type && (
                      <p className="text-red-500 text-sm mt-2">
                        <i className="fa fa-exclamation-circle mr-1"></i>
                        {errors.type.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Description */}
              <div className="md:col-span-2">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fa fa-align-left text-blue-500 mr-2"></i>
                    Business Description
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <p className="text-sm text-gray-500 !mb-3">
                    Describe your business in detail. Include services,
                    specialties, and what makes you unique.
                  </p>

                  <Controller
                    name="business_details"
                    control={control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        <div className="relative">
                          <textarea
                            {...field}
                            placeholder="Describe your business..."
                            className={`w-full border !m-0 ${
                              errors.business_details
                                ? "border-red-300 focus:ring-red-500"
                                : "border-gray-300 focus:ring-blue-500"
                            } rounded-md px-4 py-3 focus:ring-2 focus:border-transparent`}
                            rows={5}
                            maxLength={1000}
                          />
                        </div>

                        <div className="flex justify-between items-center">
                          <div>
                            {errors.business_details && (
                              <span className="text-red-500 text-sm font-medium flex items-center">
                                <i className="fa fa-exclamation-circle mr-1"></i>
                                {errors.business_details.message}
                              </span>
                            )}
                          </div>

                          <div
                            className={`text-sm ${
                              businessDetails.length < 5
                                ? "text-red-500"
                                : "text-gray-500"
                            }`}
                          >
                            {businessDetails.length}/1000
                            {businessDetails.length < 5 && (
                              <span className="ml-1">
                                ({5 - businessDetails.length} more needed)
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <i className="fa fa-lightbulb mr-2 text-yellow-500"></i>
                          <span>
                            Tip: Mention what makes your business unique!
                          </span>
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              <>
                {businessType?.includes("airport_restaurants_cafes") && (
                  <div className="md:col-span-2">
                    {compressingMenuImages && (
                      <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg">
                          <p className="text-lg font-medium">
                            Optimizing menu images...
                          </p>
                          <p className="text-gray-600">
                            Please wait while we process your menu photos.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="fa fa-utensils text-green-500 mr-2"></i>
                        Menu Images
                      </label>
                      <p className="text-sm text-gray-500 !mb-3">
                        Upload images of your menu (max 20 images, each max 1MB
                        after optimization). This helps customers see your
                        offerings.
                      </p>

                      <Controller
                        name="menu_images"
                        control={control}
                        render={({ field }) => {
                          // field.value will be an array of File objects or strings (for existing URLs)
                          const files = field.value || [];

                          return (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex flex-wrap gap-4">
                                {files.map((file, idx) => (
                                  <div key={idx} className="relative">
                                    <img
                                      src={
                                        // Check if file is a File object (newly added) or a string (existing URL)
                                        file instanceof File
                                          ? URL.createObjectURL(file)
                                          : typeof file === "string"
                                          ? file
                                          : `https://s3.amazonaws.com/flyinn-app-bucket/${file.image}`
                                      }
                                      alt={`Menu ${idx + 1}`}
                                      className="h-32 w-32 object-cover rounded-lg border shadow-sm"
                                    />
                                    <button
                                      type="button"
                                      className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-red-50 w-[20px] h-[20px] flex justify-center items-center"
                                      onClick={() => {
                                        const updated = files.filter(
                                          (_, i) => i !== idx
                                        );

                                        // If you're handling deletion of server-side images, capture `file.id` here
                                        if (
                                          !(file instanceof File) &&
                                          file?.id
                                        ) {
                                          setImagesMarkedForDeletion((prev) => [
                                            ...prev,
                                            file.id,
                                          ]);
                                        }
                                        field.onChange(updated); // Update react-hook-form state
                                        // If using createObjectURL, revoke it when no longer needed
                                        if (file instanceof File) {
                                          URL.revokeObjectURL(file);
                                        }
                                      }}
                                    >
                                      <i className="fa fa-times text-red-500 text-sm"></i>
                                    </button>
                                  </div>
                                ))}

                                {files.length < 20 && (
                                  <label
                                    htmlFor="menu-images-upload"
                                    className="cursor-pointer flex flex-col items-center justify-center h-32 w-32 border-2 border-dashed border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                                  >
                                    <i className="fa fa-plus text-gray-400 text-xl mb-2"></i>
                                    <span className="text-sm text-gray-700">
                                      Add Menu
                                    </span>
                                  </label>
                                )}
                              </div>

                              <input
                                id="menu-images-upload"
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/jpg" // Specific accept types
                                multiple
                                className="hidden"
                                // Pass the event and react-hook-form's field.onChange to the handler
                                onChange={(e) =>
                                  handleMenuImagesChange(
                                    e,
                                    field.onChange,
                                    field.value
                                  )
                                }
                                disabled={files.length >= 20}
                              />

                              <div className="text-xs text-gray-500 mt-2">
                                {files.length}/20 menu images uploaded
                              </div>

                              {errors.menu_images && (
                                <p className="text-red-500 text-sm mt-2">
                                  <i className="fa fa-exclamation-circle mr-1"></i>
                                  {errors.menu_images.message}
                                </p>
                              )}
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </>

              {/* Contact Details Section */}
              <div className="md:col-span-2 mt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <i className="fa fa-address-book text-indigo-500 mr-2"></i>
                  Contact Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Hours of Operation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fa fa-clock text-indigo-500 mr-2"></i>
                      Hours of Operation
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-sm text-gray-500 !mb-3">
                      Your regular business hours (e.g. Mon-Fri 9am-5pm)
                    </p>

                    <Controller
                      name="operational_hrs"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <input
                            {...field}
                            type="text"
                            placeholder="e.g. Mon-Fri: 9AM-5PM, Sat: 10AM-2PM"
                            className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          {errors.operational_hrs && (
                            <p className="text-red-500 text-sm mt-2">
                              <i className="fa fa-exclamation-circle mr-1"></i>
                              {errors.operational_hrs.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fa fa-phone text-indigo-500 mr-2"></i>
                      Phone Number
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-sm text-gray-500 !mb-3">
                      Primary contact number for your business
                    </p>

                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <PhoneInput
                            placeholder="e.g. (555) 123-4567"
                            defaultCountry="US"
                            international={false}
                            countryCallingCodeEditable={false}
                            withCountryCallingCode={true}
                            initialValueFormat="national"
                            value={field.value || ""}
                            onChange={(value) => {
                              if (!value || value.length <= 15) {
                                field.onChange(value);
                              }
                            }}
                            className={`w-full border rounded-md !px-3 py-3 flex ${
                              errors?.phone
                                ? "border-red-500"
                                : "border-gray-300"
                            } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                          />
                          {errors.phone && (
                            <p className="text-red-500 text-sm mt-2">
                              <i className="fa fa-exclamation-circle mr-1"></i>
                              {errors.phone.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fa fa-globe text-indigo-500 mr-2"></i>
                      Website URL
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-sm text-gray-500 !mb-3">
                      Your business website (include http:// or https://)
                    </p>

                    {/* Radio Buttons for Scheme Selection */}
                    <div className="flex items-center mb-3">
                      <label className="inline-flex items-center mr-4">
                        <Controller
                          name="url_protocol_selection" // New field for protocol selection
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              className="form-radio text-indigo-600"
                              value="https://"
                              checked={field.value === "https://"}
                            />
                          )}
                        />
                        <span className="ml-2">HTTPS</span>
                      </label>
                      <label className="inline-flex items-center">
                        <Controller
                          name="url_protocol_selection" // New field for protocol selection
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              className="form-radio text-indigo-600"
                              value="http://"
                              checked={field.value === "http://"}
                            />
                          )}
                        />
                        <span className="ml-2">HTTP</span>
                      </label>
                    </div>

                    <Controller
                      name="url" // Keep the original 'url' variable name
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <div className="flex rounded-md shadow-sm">
                            <span className="inline-flex border-solid items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                              {selectedProtocol}{" "}
                              {/* Display the actively selected protocol */}
                            </span>
                            <input
                              {...field}
                              type="text"
                              placeholder="yourbusiness.com"
                              className="flex-1 min-w-0 block w-full px-3 py-3 rounded-none rounded-r-md border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm border"
                              // On change, update the 'url' field by keeping the selected protocol
                              onChange={(e) => {
                                // Extract only the domain part from the input
                                // We remove any http:// or https:// that might have been typed
                                const domainOnly = e.target.value.replace(
                                  /^https?:\/\//,
                                  ""
                                );
                                field.onChange(
                                  `${selectedProtocol}${domainOnly}`
                                );
                              }}
                              // Display only the domain part in the input field
                              value={currentUrlValue.replace(
                                /^https?:\/\//,
                                ""
                              )}
                            />
                          </div>
                          {errors.url && (
                            <p className="text-red-500 text-sm mt-2">
                              <i className="fa fa-exclamation-circle mr-1"></i>
                              {errors.url.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Airport Details Section */}
              <div className="md:col-span-2 mt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <i className="fa fa-plane-departure text-amber-500 mr-2"></i>
                  Airport Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Distance from Runway */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fa fa-road text-amber-500 mr-2"></i>
                      Distance from Runway
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-sm text-gray-500 !mb-3">
                      How far is your business from the nearest runway?
                    </p>

                    <Controller
                      name="distance_from_runway"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <select
                            {...field}
                            className="w-full border border-gray-300 rounded-md px-4 py-3 bg-white appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select distance</option>
                            <option value={0}>0 miles (on airport)</option>
                            <option value={1}>0.1 to 1 miles</option>
                            <option value={3}>1.1 to 3 miles</option>
                            <option value={7}>3.1 to 7 miles</option>
                            <option value={8}>7+ miles</option>
                          </select>
                          <i className="absolute right-4 top-4 text-gray-400 fa fa-chevron-down"></i>
                          {errors.distance_from_runway && (
                            <p className="text-red-500 text-sm mt-2">
                              <i className="fa fa-exclamation-circle mr-1"></i>
                              {errors.distance_from_runway.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Airport Identifier */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fa fa-map-marker-alt text-amber-500 mr-2"></i>
                      Airport Identifier
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-sm text-gray-500 !mb-3">
                      4-letter airport code (e.g. KJFK, KLAX)
                    </p>

                    <Controller
                      name="airport"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <input
                            {...field}
                            type="text"
                            placeholder="e.g. KJFK"
                            maxLength={4}
                            className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                          />
                          {errors.airport && (
                            <p className="text-red-500 text-sm mt-2">
                              <i className="fa fa-exclamation-circle mr-1"></i>
                              {errors.airport.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <DiscountFields />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              {businessId ? (
                <button
                  type="button"
                  className="btn bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-bold py-4 px-10 rounded-xl shadow-lg transition-all duration-300 flex items-center transform hover:scale-105"
                  disabled={loading || creatingCustomer}
                  onClick={async () => {
                    // Trigger form validation
                    const isValid = await methods.trigger();
                    console.log({ isValid });
                    if (isValid) {
                      // Get the form values
                      const formValues = methods.getValues();

                      // Call your update function
                      await updateBusiness(formValues);
                    } else {
                      // Show error message if validation fails
                      Swal.fire({
                        icon: "error",
                        title: "Validation Error",
                        text: "Please ensure you have completed all form values",
                      });
                    }
                  }}
                >
                  {loading || creatingCustomer ? (
                    <span className="flex items-center !text-white">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    "Update"
                  )}
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-bold py-4 px-10 rounded-xl shadow-lg transition-all duration-300 flex items-center transform hover:scale-105"
                  disabled={loading || creatingCustomer}
                >
                  {loading || creatingCustomer ? (
                    <span className="flex items-center !text-white">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center !text-white">
                      Continue to Checkout
                      <svg
                        className="ml-3 w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        ></path>
                      </svg>
                    </span>
                  )}
                </button>
              )}
            </div>
          </form>
        </FormProvider>

        {/* Modal (unchanged) */}
        {isModalOpen && (
          <SimpleModal
            isOpen={isModalOpen}
            currentStep={currentStep}
            onClose={() => {
              setIsModalOpen(false);
              setShowStripe(false);
              setCurrentStep(1);
              setClientSecret(null);
            }}
          >
            {renderModalContent()}
          </SimpleModal>
        )}

        <SuccessModal
          open={showSuccessModal}
          message={
            businessId
              ? "Your business was updated successfully!"
              : "Your business was added successfully!"
          }
          onCTA={() => navigate("/business")}
        />

        {showModal && (
          <div className="fixed inset-0 z-[9999999999] bg-black bg-opacity-50 flex items-center justify-center px-4">
            <div className="bg-white max-w-6xl w-full rounded-lg shadow-lg px-6 pb-6 pt-0 overflow-y-auto max-h-[90vh]">
              <div className="sticky top-0 z-10 bg-white pt-2 pb-4 mb-4 flex justify-between items-start border-b">
                <div className="flex flex-col gap-1 pr-4">
                  <h2 className="text-xl font-semibold">
                    Select Business Type
                  </h2>
                  <p className="text-sm text-gray-600 flex items-center">
                    <i className="fa fa-info-circle text-gray-400 mr-2"></i>
                    Please select the {isEditMode ? "option" : "options"} that
                    best describe your business type from the categories below.
                  </p>
                </div>

                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl p-2 transition"
                  aria-label="Close modal"
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3  gap-8 text-sm">
                {BUSINESS_SUBTYPES.map((group) => (
                  <div key={group.category}>
                    <h3 className="text-base font-semibold text-red-600 mb-2">
                      {group.category}
                    </h3>
                    <ul className="space-y-1 !pl-0">
                      {group.subcategories.map((sub) => {
                        const selectedTypes =
                          businessType || (isEditMode ? "" : []);
                        const isSelected = isEditMode
                          ? selectedTypes === sub.type
                          : selectedTypes.includes(sub.type);
                        return (
                          <li className="flex" key={sub.type}>
                            <button
                              type="button"
                              onClick={() => {
                                if (isEditMode) {
                                  setValue("type", sub.type, {
                                    shouldValidate: true,
                                  });
                                } else {
                                  let updatedTypes = Array.isArray(
                                    selectedTypes
                                  )
                                    ? [...selectedTypes]
                                    : [];

                                  let newPlans = { ...selectedPlansAndBilling };

                                  if (isSelected) {
                                    updatedTypes = updatedTypes.filter(
                                      (t) => t !== sub.type
                                    );

                                    // Check if all values for this type are falsy
                                    const plan =
                                      selectedPlansAndBilling[sub.type];
                                    const allFalsy =
                                      plan &&
                                      Object.values(plan).every(
                                        (v) =>
                                          !v ||
                                          (typeof v === "object" &&
                                            Object.keys(v).length === 0)
                                      );

                                    if (allFalsy) {
                                      delete newPlans[sub.type];
                                      setSelectedPlansAndBilling(newPlans);
                                    }
                                  } else {
                                    updatedTypes.push(sub.type);
                                  }

                                  setValue("type", updatedTypes, {
                                    shouldValidate: true,
                                  });
                                }
                              }}
                              className={`text-left w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-blue-50 transition border ${
                                isSelected
                                  ? "font-semibold text-blue-800 border-blue-400 bg-blue-50"
                                  : "text-gray-700 border-transparent"
                              }`}
                            >
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 border border-solid ${
                                  isEditMode ? "rounded-full" : "rounded"
                                }  mr-2 ${
                                  isSelected
                                    ? "bg-blue-600 border-blue-600"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <i className="fa fa-check text-white text-xs" />
                                )}
                              </span>
                              {sub.label}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};
